import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import _ from 'underscore'
import styled from 'styled-components'

import DeviceMetricModel from 'models/DeviceMetric'

import Form from 'pages/shared/Form'
import ChartLimitTable from 'pages/shared/DeviceMetricForm/ChartLimitTable'
import MetricFields from 'pages/shared/DeviceMetricForm/MetricFields'

import { useStoreContext } from 'hooks/StoreContext'
import useDeviceMetrics from 'hooks/useDeviceMetrics'
import useTrends from 'hooks/useTrends'

import { Metric, DeviceMetricFormErrors } from 'types/DeviceMetric'

import { parseIntoDescriptionsAndDetails, convertToTextareaValue } from 'helpers/alerts'

interface props {
  title: string,
  validatedSaveType: string,
  deviceMetric: Omit<Metric, 'type'>
}

interface MetricValues extends Omit<Metric, 'callToAction'> {
  callToAction: string;
}

const deviceMetricModel = new DeviceMetricModel()

const DeviceMetricForm = ({ title, validatedSaveType, deviceMetric }: props) => {
  const navigate = useNavigate()
  useDeviceMetrics()
  useTrends()

  const { deviceMetrics, setAreDeviceMetricsLoaded, trends } = useStoreContext()
  const charts = deviceMetric.charts
  const hasDeviceMetricStatuses = deviceMetric.hasMetricStatuses
  const hasDeviceMetricValues = deviceMetric.hasMetricValues
  const displayDeleteButton = validatedSaveType === 'update' && charts.length === 0 && !hasDeviceMetricStatuses && !hasDeviceMetricValues

  const [deviceMetricAttributes, setDeviceMetricAttributes] = useState<Omit<MetricValues, 'type'>>({
    ...deviceMetric,
    callToAction: convertToTextareaValue(deviceMetric.callToAction)
  })

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState<DeviceMetricFormErrors>({ id: '', name: '', description: '', callToAction: '', color: '', unit: '', draft: '' })
  const onSaveCallback = useCallback(() => {
    setDeviceMetricAttributes({ ...deviceMetricAttributes })
    setIsSaving(true)
    const onSaveSuccessCallback = () => {
      setAreDeviceMetricsLoaded(false)
      navigate('/admin/device_metrics')
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallBack = () => {
      setIsSaving(false)
      alert('Save Failed')
    }
    deviceMetricModel.save(deviceMetrics, trends, validatedSaveType, {
      ...deviceMetricAttributes,
      callToAction: parseIntoDescriptionsAndDetails(deviceMetricAttributes.callToAction)
    }, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceMetricAttributes, validatedSaveType, setAreDeviceMetricsLoaded, navigate])

  const onCancelCallback = useCallback(() => {
    const hasChanges = !_.isEqual(deviceMetricAttributes, { ...deviceMetric })

    if (!hasChanges || (hasChanges && confirm('Are you sure you want to cancel?'))) {
      navigate('/admin/device_metrics')
    }
  }, [navigate, deviceMetric, deviceMetricAttributes])

  const onDeviceMetricUpdateCallback = useCallback((updatedAttributes) => {
    setDeviceMetricAttributes({ ...deviceMetricAttributes, ...updatedAttributes })
  }, [deviceMetricAttributes])

  const onDeleteCallback = useCallback(() => {
    if (confirm('Are you sure you want to delete this device metric? This cannot be undone.')) {
      setIsDeleting(true)
      const onDeleteSuccessCallback = () => {
        setAreDeviceMetricsLoaded(false)
        navigate('/admin/device_metrics')
      }

      const onDeleteFailureCallback = (errorMessage) => {
        setIsDeleting(false)
        alert(errorMessage)
      }

      deviceMetricModel.delete(onDeleteSuccessCallback, onDeleteFailureCallback, deviceMetric)
    }
  }, [navigate, deviceMetric, setAreDeviceMetricsLoaded])

  return (
    <S.Form
    deleteButtonText="Metric"
    title={title}
    isSaving={isSaving}
    isDeleting={isDeleting}
    displayDeleteButton={displayDeleteButton}
    onSaveCallback={onSaveCallback}
    onDeleteCallback={onDeleteCallback}
    onCancelCallback={onCancelCallback}
    >
      <MetricFields
        validatedSaveType={validatedSaveType}
        id={deviceMetricAttributes.id}
        name={deviceMetricAttributes.name}
        description={deviceMetricAttributes.description}
        callToAction={deviceMetricAttributes.callToAction}
        unit={deviceMetricAttributes.unit}
        draft={deviceMetricAttributes.draft}
        color={deviceMetricAttributes.color}
        onChangeCallback={onDeviceMetricUpdateCallback}
        errors={errors}
      />
      <br />
      {
        validatedSaveType === 'update' &&
          <>
            <h2> Charts </h2>
            <ChartLimitTable charts={charts} />
          </>
      }

    </S.Form>
  )
}

export default DeviceMetricForm

const S = {
  Form: styled(Form)`
    max-width: 100%;
  `
}
